/**
 * Opening and closing the preview pane — one path for the toolbar toggle, the pane's close button and the tab close
 * guard: unsaved edits are never dropped without asking (`openConfirmDialog`), and every toggle is tracked.
 */
import type { StorageExplorerMode } from '../../core/types/workspaceTypes';
import { openConfirmDialog } from '../../common/components/confirm/confirmDialogStore';
import { trackPreviewEdit, trackPreviewToggle } from './storagePreviewAnalytics';
import { hasUnsavedStoragePreview, isStoragePreviewDirty, useStoragePreviewSessionStore } from './storagePreviewSessionStore';
import { useStoragePreviewStore } from './storagePreviewStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';

/** Runs `proceed` at once when the tab has no unsaved edit, otherwise after the user agreed to discard it. */
export function confirmDiscardStoragePreview(tabId: string, prompt: { title: string; confirmLabel: string }, proceed: () => void, mode?: StorageExplorerMode): void {
  const session = useStoragePreviewSessionStore.getState().byTab[tabId];
  const discard = () => {
    useStoragePreviewSessionStore.getState().end(tabId);
    proceed();
  };
  if (!isStoragePreviewDirty(session)) {
    discard();
    return;
  }
  openConfirmDialog({
    title: prompt.title,
    description: `Your unsaved edits to ${session?.name ?? 'the file in the preview'} will be lost.`,
    confirmLabel: prompt.confirmLabel,
    variant: 'destructive',
    onConfirm: () => {
      if (mode) trackPreviewEdit('discard', 'text', mode);
      discard();
    },
  });
}

/** Closing one storage tab (its ×, an empty state's Close): asks first when its preview holds an unsaved edit. */
export function closeStorageTab(tabId: string, mode: StorageExplorerMode, removeTab: (tabId: string) => void): void {
  confirmDiscardStoragePreview(tabId, { title: 'Close the tab and discard your changes?', confirmLabel: 'Discard and close' }, () => removeTab(tabId), mode);
}

/**
 * Several tabs closing at once (Close others / to the right / unpinned, a session switch): when any of them holds an
 * unsaved preview edit, one prompt names how many; agreeing goes on (the caller discards the edits of the tabs that
 * really close — `discardStoragePreviewEdits`), cancelling closes nothing.
 */
export function confirmDiscardStoragePreviews(tabIds: readonly string[], proceed: () => void): void {
  const dirty = tabIds.filter(hasUnsavedStoragePreview);
  if (dirty.length === 0) {
    proceed();
    return;
  }
  openConfirmDialog({
    title: dirty.length === 1 ? 'Close the tab and discard your changes?' : 'Close the tabs and discard your changes?',
    description: dirty.length === 1
      ? `Your unsaved edits to ${useStoragePreviewSessionStore.getState().byTab[dirty[0]]?.name ?? 'the file in the preview'} will be lost.`
      : `${dirty.length} storage tabs have file edits in their preview that are not saved. Closing discards them.`,
    confirmLabel: 'Discard and close',
    variant: 'destructive',
    onConfirm: proceed,
  });
}

/** Drops the edit sessions of tabs that are closing (their bodies unmount anyway; this keeps the order explicit). */
export function discardStoragePreviewEdits(tabIds: readonly string[]): void {
  const sessions = useStoragePreviewSessionStore.getState();
  tabIds.forEach((tabId) => {
    if (hasUnsavedStoragePreview(tabId)) {
      const tab = useWorkspaceStore.getState().tabs?.find((candidate) => candidate.id === tabId);
      if (tab?.type === 'storage-explorer') trackPreviewEdit('discard', 'text', tab.content.mode);
    }
    sessions.end(tabId);
  });
}

export function openStoragePreview(mode: StorageExplorerMode): void {
  useStoragePreviewStore.getState().setOpen(true);
  trackPreviewToggle(true, mode);
}

export function closeStoragePreview(tabId: string, mode: StorageExplorerMode): void {
  confirmDiscardStoragePreview(tabId, { title: 'Close the preview and discard your changes?', confirmLabel: 'Discard and close' }, () => {
    useStoragePreviewStore.getState().setOpen(false);
    trackPreviewToggle(false, mode);
    // The pane (and whatever had focus in it) is gone: the keyboard continues from the toggle that brings it back.
    requestAnimationFrame(() => {
      const toggles = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="storage-explorer-tab"] [data-testid="storage-toolbar-preview"]'));
      toggles.find((toggle) => toggle.offsetParent !== null)?.focus();
    });
  }, mode);
}
